<template>
  <div class="map-container">
    <div :id="mapId" class="map-content"></div>
    <drag-box ref="dragBox"></drag-box>
  </div>
</template>

<script>
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import XYZ from 'ol/source/XYZ';
import * as olProj from 'ol/proj';
import { getUUID } from '@/utils/util';
import TileLayer from 'ol/layer/Tile';
import ImageLayer from 'ol/layer/Image';
import ImageWMS from 'ol/source/ImageWMS';
import { defaults as defaultInteractions } from 'ol/interaction';
import { unByKey } from 'ol/Observable';
import storage from 'store';
import { LonLat } from '@/store/mutation-types';
import ScaleLine from 'ol/control/ScaleLine';
import Polygon from 'ol/geom/Polygon';
import Feature from 'ol/Feature';
import { geoserverWFS } from '@/api/manage';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Text from 'ol/style/Text';
import LineString from 'ol/geom/LineString';
import Point from 'ol/geom/Point';
import Circle from 'ol/style/Circle';
import Icon from 'ol/style/Icon';
import { calculateDistance, calculateTotalDistance, formatDistance, calculatePolygonArea, formatArea } from '@/utils/geoUtils';
import dragBox from '../dragBox';

export default {
  name: 'GisMap',
  components: {
    dragBox
  },
  data () {
    return {
      mapId: getUUID(), // 地图id
      map: null, // 地图实例
      baseLayer: null, // 底图图层
      selectedItemId: null, // 选中的图层ID
      clickTypeList: [], // WMS点击事件列表
      loadedLayers: {}, // 存储已加载的图层配置，key为图层id，value为图层配置对象
      currentLayerLevel: 1, // 当前图层等级
      scaleLineControl: null, // 比例尺控件
      zoneMeteringVectorLayer: null, // 片区范围面图层
      zoneMeteringVectorSource: null, // 片区范围面数据源
      vectorSource: null, // 点击标记矢量数据源
      vectorLayer: null, // 点击标记矢量图层
      toolList: {
        measure: [{ name: '测距', type: 'measure-distance', selectable: true }, { name: '测面', type: 'measure-surface', selectable: true }],
        export: [{ name: '导出图片', type: 'export-image', selectable: false }, { name: '框选打印', type: 'export-print', selectable: false }, { name: '打印', type: 'print', selectable: false }]
      },
      currentToolList: [], // 当前显示的工具列表
      showToolContainer: false, // 是否显示工具容器
      activeToolType: null, // 当前激活的工具类型
      // 测距相关属性
      measureDistancePoints: [], // 测距点集合
      measureDistanceSource: null, // 测距矢量数据源
      measureDistanceLayer: null, // 测距图层
      measureDistanceTotalLabel: null, // 总距离标签
      // 测面相关属性
      measureSurfacePoints: [], // 测面点集合
      measureSurfaceSource: null, // 测面矢量数据源
      measureSurfaceLayer: null, // 测面图层
      measureSurfacePolygon: null, // 测面多边形
      // 地图点击事件监听器
      mapClickListener: null,
      // 片区设备高亮图层
      zoneDevicesSource: null, // 片区设备矢量数据源
      zoneDevicesLayer: null // 片区设备矢量图层
    };
  },
  mounted () {
    console.log('GisMap组件已挂载，地图ID:', this.mapId);
    this.$nextTick(() => {
      console.log('DOM更新后，开始初始化地图...');
      this.initMap(); // 初始化地图
    });
  },
  beforeDestroy () {
    console.log('开始销毁地图组件，清理资源...');

    // 清除所有 WMS 图层的点击事件
    if (this.clickTypeList && this.clickTypeList.length > 0) {
      Object.keys(this.clickTypeList).forEach(key => {
        if (this.clickTypeList[key]) {
          unByKey(this.clickTypeList[key]);
          this.clickTypeList[key] = null;
        }
      });
      this.clickTypeList = [];
    }

    // 移除比例尺控件
    if (this.scaleLineControl && this.map) {
      this.map.removeControl(this.scaleLineControl);
      this.scaleLineControl = null;
    }

    // 移除所有图层
    if (this.map) {
      const layers = this.map.getLayers().getArray().slice(); // 复制数组避免迭代时修改
      layers.forEach(layer => {
        if (layer) {
          this.map.removeLayer(layer);
          // 清理图层资源
          if (layer.getSource()) {
            const source = layer.getSource();
            if (source.clear && typeof source.clear === 'function') {
              source.clear();
            }
            if (source.dispose && typeof source.dispose === 'function') {
              source.dispose();
            }
          }
          if (layer.dispose && typeof layer.dispose === 'function') {
            layer.dispose();
          }
        }
      });
    }

    // 清除片区范围面图层
    this.clearZoneMeteringPolygon();

    // 清除点击标记矢量图层
    if (this.vectorLayer && this.map) {
      this.map.removeLayer(this.vectorLayer);
      if (this.vectorSource) {
        this.vectorSource.clear();
        if (this.vectorSource.dispose && typeof this.vectorSource.dispose === 'function') {
          this.vectorSource.dispose();
        }
        this.vectorSource = null;
      }
      if (this.vectorLayer.dispose && typeof this.vectorLayer.dispose === 'function') {
        this.vectorLayer.dispose();
      }
      this.vectorLayer = null;
    }

    // 清除工具容器状态
    this.showToolContainer = false;
    this.currentToolList = [];
    this.activeToolType = null;
    this.clearMapCursorClass();

    // 清除测量数据
    this.clearMeasureDistance();
    this.clearMeasureSurface();

    // 移除地图点击事件监听器
    if (this.mapClickListener) {
      unByKey(this.mapClickListener);
      this.mapClickListener = null;
    }

    // 清空所有图层引用
    this.baseLayer = null;

    // 销毁地图实例
    if (this.map) {
      // 移除所有交互
      const interactions = this.map.getInteractions().getArray().slice();
      interactions.forEach(interaction => {
        this.map.removeInteraction(interaction);
      });

      // 移除所有叠加层
      const overlays = this.map.getOverlays().getArray().slice();
      overlays.forEach(overlay => {
        this.map.removeOverlay(overlay);
      });

      this.map.setTarget(null);
      this.map.dispose();
      this.map = null;
    }

    console.log('地图组件资源清理完成');
  },
  methods: {
    // 处理底图变化事件
    handleLayerChange (data) {
      console.log('GisMap 接收到底图变化:', data);
      // 先清除之前的底图
      this.clearBaseLayer();
      // 创建新的底图，传递完整的配置对象
      this.createBaseLayer(data);
    },

    // 处理图层选择事件
    handleLayerSelected (data) {
      console.log('图层选择事件:', data);

      if (data.selected) {
        // 判断是否是片区范围示意图图层
        const isZoneMeteringLayer =
          data.item.name === '片区范围示意图' ||
          data.item.dataSource === 'smart_water:zone_metering' ||
          (data.item.dataSource && data.item.dataSource.includes('zone_metering'));

        let layerConfig = { ...data.item };

        // 如果是片区范围示意图，使用当前等级更新 SQL 条件
        if (isZoneMeteringLayer) {
          const tenantId = this.$store?.state?.user?.info?.tenantId || 'default-super-tenant';
          // 更新 showFilterCondition，使用当前的等级值
          layerConfig.showFilterCondition = `is_deleted = false and level = ${this.currentLayerLevel} and tenant_id = '${tenantId}'`;
          console.log('片区范围示意图重新显示，使用当前等级:', this.currentLayerLevel, 'SQL:', layerConfig.showFilterCondition);
        }

        // 存储图层配置（保留更新后的配置）
        this.loadedLayers[data.item.id] = layerConfig;
        this.getWmsLayers(layerConfig);
      } else {
        // 隐藏图层时，保留配置（不删除），只从地图中移除图层
        // 这样重新显示时可以使用更新后的配置
        this.removeWmsLayers(data.item);

        // 如果是片区范围示意图，清除面图层
        const isZoneMeteringLayer =
          data.item.name === '片区范围示意图' ||
          data.item.dataSource === 'smart_water:zone_metering' ||
          (data.item.dataSource && data.item.dataSource.includes('zone_metering'));

        if (isZoneMeteringLayer) {
          this.clearZoneMeteringPolygon();
        }
      }
    },

    // 处理图层等级变更
    handleLayerLevelChange (level) {
      console.log('GisMap 接收到图层等级变更:', level);

      // 清除片区范围面图层（切换等级时清除）
      this.clearZoneMeteringPolygon();
      // 清除 loadZoneDevices 相关的设备图标
      this.clearZoneDevicesLayer();

      // 保存当前等级
      this.currentLayerLevel = level;

      // 获取当前租户ID
      const tenantId = this.$store?.state?.user?.info?.tenantId || 'default-super-tenant';

      // 从地图实例中查找所有已加载的图层
      if (this.map) {
        const allLayers = this.map.getLayers().getArray();

        allLayers.forEach(olLayer => {
          const layerId = olLayer.get('id');
          const layerName = olLayer.get('name');
          const source = olLayer.getSource();

          // 判断是否是片区范围示意图图层
          // 通过图层ID、名称或数据源判断
          const isZoneMeteringLayer =
            (layerName && layerName === '片区范围示意图') ||
            (layerId && (
              layerId.includes('zone_metering') ||
              layerId === '片区范围示意图'
            )) ||
            (source && source instanceof ImageWMS && source.getParams().LAYERS &&
              source.getParams().LAYERS.includes('zone_metering'));

          if (isZoneMeteringLayer) {
            console.log('找到片区范围示意图图层，更新SQL条件:', layerId || layerName);

            // 获取对应的图层配置（优先从 loadedLayers 获取）
            let layerConfig = this.loadedLayers[layerId];

            // 如果没有在 loadedLayers 中找到，尝试从图层源中获取
            if (!layerConfig && source instanceof ImageWMS) {
              const params = source.getParams();
              layerConfig = {
                id: layerId || 'zone_metering',
                name: layerName || '片区范围示意图',
                dataSource: params.LAYERS,
                workspace: 'smart_water',
                showFilterCondition: params.CQL_FILTER || ''
              };
            }

            if (layerConfig) {
              // 更新 showFilterCondition，将 level = 1 改为切换的 level 值
              // 原SQL: is_deleted = false and level = 1 and tenant_id = 'default-super-tenant'
              const newFilterCondition = `is_deleted = false and level = ${level} and tenant_id = '${tenantId}'`;

              // 更新图层配置
              layerConfig.showFilterCondition = newFilterCondition;
              this.loadedLayers[layerConfig.id] = layerConfig;

              // 重新加载图层
              this.removeWmsLayers({ id: layerConfig.id });
              this.getWmsLayers(layerConfig);
            }
          }
        });
      }
    },

    // 处理关闭覆盖层事件
    handleCloseOverlay () {
      console.log('GisMap 接收到关闭覆盖层事件');
      // 清除点击标记矢量图层
      this.clearVectorLayer();
      // 清除片区范围面图层及相关WMS图层
      this.clearZoneMeteringPolygon();
    },

    // 更新片区范围示意图WMS图层的查询条件
    updateZoneMeteringLayerFilter (zoneId, usePidStr = false) {
      if (!this.map) {
        console.warn('地图实例不存在，无法更新图层');
        return;
      }

      const tenantId = this.$store?.state?.user?.info?.tenantId || '';
      // 如果 usePidStr 为 true，使用 pid_str 条件；否则使用 zone_id 条件
      const newFilterCondition = usePidStr
        ? `is_deleted = false and pid_str = '${zoneId}' and tenant_id = '${tenantId}'`
        : `is_deleted = false and zone_id = '${zoneId}' and tenant_id = '${tenantId}'`;

      // 从地图实例中查找片区范围示意图图层
      const allLayers = this.map.getLayers().getArray();

      allLayers.forEach(olLayer => {
        const layerId = olLayer.get('id');
        const layerName = olLayer.get('name');
        const source = olLayer.getSource();

        // 判断是否是片区范围示意图图层
        const isZoneMeteringLayer =
          (layerName && layerName === '片区范围示意图') ||
          (layerId && (
            layerId.includes('zone_metering') ||
            layerId === '片区范围示意图'
          )) ||
          (source && source instanceof ImageWMS && source.getParams().LAYERS &&
            source.getParams().LAYERS.includes('zone_metering'));

        if (isZoneMeteringLayer) {
          console.log('找到片区范围示意图图层，更新查询条件:', layerId || layerName, 'zoneId:', zoneId);

          // 获取对应的图层配置（优先从 loadedLayers 获取）
          let layerConfig = this.loadedLayers[layerId];

          // 如果没有在 loadedLayers 中找到，尝试从图层源中获取
          if (!layerConfig && source instanceof ImageWMS) {
            const params = source.getParams();
            layerConfig = {
              id: layerId || 'zone_metering',
              name: layerName || '片区范围示意图',
              dataSource: params.LAYERS,
              workspace: 'smart_water',
              showFilterCondition: params.CQL_FILTER || ''
            };
          }

          if (layerConfig) {
            // 更新 showFilterCondition
            layerConfig.showFilterCondition = newFilterCondition;
            this.loadedLayers[layerConfig.id] = layerConfig;

            // 重新加载图层
            this.removeWmsLayers({ id: layerConfig.id });
            this.getWmsLayers(layerConfig);
          }
        }
      });
    },

    // 处理 selectedID 变化事件
    handleSelectedIdChange (data) {
      console.log('GisMap 接收到 selectedID 变化:', data);

      if (data.item) {
        this.selectedItemId = data.item.id;
      } else {
        this.selectedItemId = null;
      }
    },

    // 清除图层
    clearLayerById (layerId) {
      console.log('GisMap 接收到清除图层事件:', layerId);
      this.removeWmsLayers({ id: layerId });
    },

    // 处理定位事件
    handlePositioning () {
      console.log('GisMap 接收到定位事件');
      if (this.map) {
        this.map.getView().setCenter(storage.get(LonLat));
      }
    },

    // 处理片区选择变更
    handleZoneNameChange (val) {
      console.log('GisMap 接收到片区选择变更:', val);

      if (!val || !val.key) {
        console.warn('片区选择变更: 缺少片区ID');
        return;
      }

      const zoneId = val.key;
      const tenantId = this.$store?.state?.user?.info?.tenantId || 'default-super-tenant';

      // 构建查询条件
      const cql = `is_deleted = false and zone_id = '${zoneId}' and tenant_id = '${tenantId}'`;

      console.log('查询片区数据，CQL:', cql);

      // 通过 geoserverWFS 获取片区几何数据
      geoserverWFS('smart_water', 'zone_metering', '&CQL_FILTER=' + cql).then((res) => {
        if (res.data && res.data.features && res.data.features.length > 0) {
          const feature = res.data.features[0];

          // 获取几何坐标
          if (feature.geometry && feature.geometry.coordinates) {
            // 创建面图层（和点击片区范围示意图一样的效果）
            // showZoneMeteringPolygon 内部会处理缩放和定位
            this.showZoneMeteringPolygon(feature);

            console.log('地图已定位到选中片区:', zoneId);

            // 触发 feature-selected 事件，显示详情面板（和点击片区范围示意图一样的效果）
            this.$emit('feature-selected', feature);
          } else {
            console.warn('片区数据缺少几何信息');
          }
        } else {
          console.warn('未找到片区数据:', zoneId);
        }
      }).catch((error) => {
        console.error('获取片区数据失败:', error);
      });
    },

    // 处理查询事件
    handleQuerySearch (queryParams) {
      console.log('GisMap 接收到查询事件:', queryParams);
      // 清除之前的图层
      this.removeWmsLayers({ id: 'query_in_query_drawer' });
      // 添加查询图层
      if (queryParams) {
        this.getWmsLayers(queryParams);
      }
    },

    // 地图定位（不添加标记点）
    setMapLocation (longitude, latitude, zoom = 18) {
      if (this.map) {
        const view = this.map.getView();
        view.setCenter([longitude, latitude]);
        view.setZoom(zoom);
      }
    },

    // 获取地图实例
    getBigMap () {
      return this.map;
    },

    // 处理工具点击事件
    handleToolClick (key) {
      console.log('GisMap 接收到工具点击事件:', key);

      // 切换工具时，清除之前的测量数据
      if (key !== 'measure') {
        this.clearMeasureDistance();
        this.clearMeasureSurface();
        this.activeToolType = null;
        this.clearMapCursorClass();
      }

      // 根据key获取对应的工具列表
      if (this.toolList[key]) {
        this.currentToolList = this.toolList[key];
        this.showToolContainer = true;
        console.log('显示工具列表:', this.currentToolList);
      } else {
        console.warn('未找到对应的工具列表:', key);
        this.showToolContainer = false;
      }
    },

    // 处理工具项点击
    handleToolItemClick (item) {
      console.log('工具项点击:', item);

      // 只有可选择的工具才能切换选中状态
      if (item.selectable) {
        // 清除之前的光标类名
        this.clearMapCursorClass();

        // 切换选中状态
        if (this.activeToolType === item.type) {
          // 如果当前工具已选中，则取消选中
          this.activeToolType = null;
          // 取消选中时，清除对应的测量数据
          if (item.type === 'measure-distance') {
            this.clearMeasureDistance();
          } else if (item.type === 'measure-surface') {
            this.clearMeasureSurface();
          }
        } else {
          // 否则选中当前工具
          // 切换工具时，清除之前的测量数据
          if (this.activeToolType === 'measure-distance') {
            this.clearMeasureDistance();
          } else if (this.activeToolType === 'measure-surface') {
            this.clearMeasureSurface();
          }
          this.activeToolType = item.type;
          // 添加对应的光标类名
          this.addMapCursorClass(item.type);
        }
      } else {
        // 不可选择的工具（如导出、打印），清除所有测量数据
        this.clearMeasureDistance();
        this.clearMeasureSurface();
        this.activeToolType = null;
        this.clearMapCursorClass();
      }

      // 根据工具类型执行相应操作
      switch (item.type) {
        case 'measure-distance':
          console.log('执行测距功能');
          this.clearMeasureSurface(); // 清除测面
          break;
        case 'measure-surface':
          console.log('执行测面功能');
          this.clearMeasureDistance(); // 清除测距
          break;
        case 'export-image':
          console.log('执行导出图片功能');
          this.exportMap();
          break;
        case 'export-print':
          console.log('执行框选打印功能');
          if (this.$refs.dragBox && this.$refs.dragBox.selectBoxPrint) {
            this.$refs.dragBox.selectBoxPrint();
          } else {
            this.$message.warning('dragBox 组件未初始化');
          }
          break;
        case 'print':
          console.log('执行打印功能');
          this.printMap();
          break;
      }
    },

    // 处理删除工具按钮点击
    handleDeleteTool () {
      console.log('关闭工具栏');
      this.showToolContainer = false;
      this.currentToolList = [];
      this.activeToolType = null; // 清除选中状态
      this.clearMapCursorClass(); // 清除光标类名
      // 清除测量数据
      this.clearMeasureDistance();
      this.clearMeasureSurface();
    },

    // 添加地图光标类名
    addMapCursorClass (toolType) {
      const mapElement = document.getElementById(this.mapId);
      if (mapElement) {
        mapElement.classList.add(`cursor-${toolType}`);
      }
    },

    // 清除地图光标类名
    clearMapCursorClass () {
      const mapElement = document.getElementById(this.mapId);
      if (mapElement) {
        // 移除所有光标相关的类名
        const classes = mapElement.className.split(' ').filter(cls => !cls.startsWith('cursor-'));
        mapElement.className = classes.join(' ');
      }
    },

    // 地图点击事件处理
    clickMap (event) {
      if (!this.map) return;

      // 检查是否点击了要素（只检查设备图层和测量图层）
      const pixel = this.map.getEventPixel(event.originalEvent);
      const feature = this.map.forEachFeatureAtPixel(pixel, (feature) => feature, {
        layerFilter: (layer) => {
          // 只检查设备图层和测量图层，不影响其他图层（如 WMS 图层）的点击
          return layer === this.zoneDevicesLayer ||
            layer === this.measureDistanceLayer ||
            layer === this.measureSurfaceLayer;
        }
      });

      if (feature) {
        // 检查是否点击了设备图标（分区计量仪表或分界阀门）
        const featureType = feature.get('type');
        if (featureType === 'remoteMeter' || featureType === 'boundaryValve') {
          this.handleDeviceClick(feature);
          return;
        }
        // 如果点击了其他要素，不处理测量功能
        return;
      }

      // 根据激活的工具类型执行相应操作
      if (this.activeToolType === 'measure-distance') {
        this.measureDistance(event.coordinate);
      } else if (this.activeToolType === 'measure-surface') {
        this.measureSurface(event.coordinate);
      }
    },

    // 初始化测距图层
    initMeasureDistanceLayer () {
      this.measureDistanceSource = new VectorSource();
      this.measureDistanceLayer = new VectorLayer({
        source: this.measureDistanceSource,
        zIndex: 1002,
        style: (feature) => {
          const type = feature.get('type');
          if (type === 'measure-point') {
            return new Style({
              image: new Circle({
                radius: 6,
                fill: new Fill({
                  color: '#ff4757'
                }),
                stroke: new Stroke({
                  color: '#ffffff',
                  width: 2
                })
              })
            });
          } else if (type === 'measure-line') {
            return new Style({
              stroke: new Stroke({
                color: '#ff4757',
                width: 3,
                lineDash: [5, 5]
              })
            });
          } else if (type === 'measure-label') {
            return new Style({
              text: new Text({
                text: feature.get('text'),
                font: '14px Arial',
                fill: new Fill({
                  color: '#ff4757'
                }),
                stroke: new Stroke({
                  color: '#ffffff',
                  width: 2
                }),
                offsetY: -15
              })
            });
          } else if (type === 'measure-total-label') {
            return new Style({
              text: new Text({
                text: feature.get('text'),
                font: '16px Arial',
                fill: new Fill({
                  color: '#ff4757'
                }),
                stroke: new Stroke({
                  color: '#ffffff',
                  width: 3
                }),
                offsetY: 20
              })
            });
          }
          return null;
        }
      });
      if (this.map) {
        this.map.addLayer(this.measureDistanceLayer);
      }
    },

    // 初始化测面图层
    initMeasureSurfaceLayer () {
      this.measureSurfaceSource = new VectorSource();
      this.measureSurfaceLayer = new VectorLayer({
        source: this.measureSurfaceSource,
        zIndex: 1002,
        style: (feature) => {
          const type = feature.get('type');
          if (type === 'measure-surface-point') {
            return new Style({
              image: new Circle({
                radius: 6,
                fill: new Fill({
                  color: '#52c41a'
                }),
                stroke: new Stroke({
                  color: '#ffffff',
                  width: 2
                })
              })
            });
          } else if (type === 'measure-surface-line') {
            return new Style({
              stroke: new Stroke({
                color: '#52c41a',
                width: 3,
                lineDash: [5, 5]
              })
            });
          } else if (type === 'measure-surface-polygon') {
            return new Style({
              fill: new Fill({
                color: 'rgba(82, 196, 26, 0.2)'
              }),
              stroke: new Stroke({
                color: '#52c41a',
                width: 2
              })
            });
          } else if (type === 'measure-surface-label') {
            return new Style({
              text: new Text({
                text: feature.get('text'),
                font: '16px Arial',
                fill: new Fill({
                  color: '#52c41a'
                }),
                stroke: new Stroke({
                  color: '#ffffff',
                  width: 3
                }),
                offsetY: -20
              })
            });
          }
          return null;
        }
      });
      if (this.map) {
        this.map.addLayer(this.measureSurfaceLayer);
      }
    },

    // 测距
    measureDistance (coordinate) {
      // 添加测距点
      this.measureDistancePoints.push(coordinate);

      // 创建测距点要素
      const point = new Point(coordinate);
      const pointFeature = new Feature({
        geometry: point,
        type: 'measure-point',
        index: this.measureDistancePoints.length - 1
      });

      // 添加到测距图层
      this.measureDistanceSource.addFeature(pointFeature);

      // 如果有多个点，创建线段
      if (this.measureDistancePoints.length > 1) {
        const lineCoordinates = this.measureDistancePoints.slice(-2);
        const lineString = new LineString(lineCoordinates);
        const lineFeature = new Feature({
          geometry: lineString,
          type: 'measure-line',
          index: this.measureDistancePoints.length - 2
        });

        this.measureDistanceSource.addFeature(lineFeature);

        // 计算并显示距离
        const distance = calculateDistance(lineCoordinates[0], lineCoordinates[1]);
        this.showDistanceLabel(lineCoordinates[1], distance);

        // 移除之前的总距离标签
        if (this.measureDistanceTotalLabel) {
          this.measureDistanceSource.removeFeature(this.measureDistanceTotalLabel);
          this.measureDistanceTotalLabel = null;
        }

        // 计算并显示总距离
        const totalDistance = calculateTotalDistance(this.measureDistancePoints);
        this.showTotalDistanceLabel(coordinate, totalDistance);
      }
    },

    // 显示距离标签
    showDistanceLabel (coordinate, distance) {
      const distanceText = formatDistance(distance);

      // 创建距离标签要素
      const point = new Point(coordinate);
      const labelFeature = new Feature({
        geometry: point,
        type: 'measure-label',
        text: distanceText
      });

      this.measureDistanceSource.addFeature(labelFeature);
    },

    // 显示总距离标签
    showTotalDistanceLabel (coordinate, totalDistance) {
      const totalDistanceText = `总距离: ${formatDistance(totalDistance)}`;

      // 创建总距离标签要素
      const point = new Point(coordinate);
      const labelFeature = new Feature({
        geometry: point,
        type: 'measure-total-label',
        text: totalDistanceText
      });

      this.measureDistanceSource.addFeature(labelFeature);
      this.measureDistanceTotalLabel = labelFeature;
    },

    // 测面
    measureSurface (coordinate) {
      // 添加测面点
      this.measureSurfacePoints.push(coordinate);

      // 创建测面点要素
      const point = new Point(coordinate);
      const pointFeature = new Feature({
        geometry: point,
        type: 'measure-surface-point',
        index: this.measureSurfacePoints.length - 1
      });

      // 添加到测面图层
      this.measureSurfaceSource.addFeature(pointFeature);

      // 如果有多个点，创建线段或多边形
      if (this.measureSurfacePoints.length > 1) {
        // 创建线段
        const lineCoordinates = this.measureSurfacePoints.slice(-2);
        const lineString = new LineString(lineCoordinates);
        const lineFeature = new Feature({
          geometry: lineString,
          type: 'measure-surface-line',
          index: this.measureSurfacePoints.length - 2
        });

        this.measureSurfaceSource.addFeature(lineFeature);

        // 如果有3个或更多点，创建多边形
        if (this.measureSurfacePoints.length >= 3) {
          this.updateSurfacePolygon();
        }
      }
    },

    // 更新测面多边形
    updateSurfacePolygon () {
      // 移除之前的多边形
      if (this.measureSurfacePolygon) {
        this.measureSurfaceSource.removeFeature(this.measureSurfacePolygon);
      }

      // 移除之前的标签
      const features = this.measureSurfaceSource.getFeatures();
      features.forEach(f => {
        if (f.get('type') === 'measure-surface-label') {
          this.measureSurfaceSource.removeFeature(f);
        }
      });

      // 创建新的多边形（闭合多边形）
      const polygonCoordinates = [...this.measureSurfacePoints];
      if (polygonCoordinates.length > 2) {
        // 确保多边形闭合
        const closedCoordinates = [...polygonCoordinates, polygonCoordinates[0]];

        const polygon = new Polygon([closedCoordinates]);
        const polygonFeature = new Feature({
          geometry: polygon,
          type: 'measure-surface-polygon'
        });

        this.measureSurfaceSource.addFeature(polygonFeature);
        this.measureSurfacePolygon = polygonFeature;

        // 计算并显示面积
        const area = calculatePolygonArea(this.measureSurfacePoints);
        this.showSurfaceLabel(polygonCoordinates[0], area);
      }
    },

    // 显示面积标签
    showSurfaceLabel (coordinate, area) {
      const areaText = formatArea(area);

      // 创建面积标签要素
      const point = new Point(coordinate);
      const labelFeature = new Feature({
        geometry: point,
        type: 'measure-surface-label',
        text: areaText
      });

      this.measureSurfaceSource.addFeature(labelFeature);
    },

    // 清除测距
    clearMeasureDistance () {
      this.measureDistancePoints = [];
      this.measureDistanceTotalLabel = null;
      if (this.measureDistanceSource) {
        this.measureDistanceSource.clear();
      }
      if (this.measureDistanceLayer && this.map) {
        this.map.removeLayer(this.measureDistanceLayer);
        this.measureDistanceLayer = null;
        this.measureDistanceSource = null;
        // 重新初始化图层
        this.initMeasureDistanceLayer();
      }
    },

    // 清除测面
    clearMeasureSurface () {
      this.measureSurfacePoints = [];
      this.measureSurfacePolygon = null;
      if (this.measureSurfaceSource) {
        this.measureSurfaceSource.clear();
      }
      if (this.measureSurfaceLayer && this.map) {
        this.map.removeLayer(this.measureSurfaceLayer);
        this.measureSurfaceLayer = null;
        this.measureSurfaceSource = null;
        // 重新初始化图层
        this.initMeasureSurfaceLayer();
      }
    },

    // 打印
    printMap () {
      window.print();
    },

    // 导出地图为图片
    exportMap () {
      try {
        console.log('开始导出地图图片...');

        this.map.once('rendercomplete', () => {
          const mapCanvas = document.createElement('canvas');
          const size = this.map.getSize();

          if (!size || size[0] === 0 || size[1] === 0) {
            console.error('地图尺寸无效:', size);
            this.$message.error('导出失败，地图尺寸无效');
            return;
          }

          mapCanvas.width = size[0];
          mapCanvas.height = size[1];
          const mapContext = mapCanvas.getContext('2d');

          if (!mapContext) {
            console.error('无法获取Canvas上下文');
            this.$message.error('导出失败，无法获取Canvas上下文');
            return;
          }

          // 绘制所有图层到canvas
          const canvases = document.querySelectorAll('.ol-layer canvas');
          Array.prototype.forEach.call(canvases, (canvas) => {
            if (canvas.width > 0) {
              const opacity = canvas.parentNode.style.opacity || '1';
              mapContext.globalAlpha = opacity === '' ? 1 : Number(opacity);

              const transform = canvas.style.transform;
              if (transform && transform.includes('matrix')) {
                const matrixMatch = transform.match(/^matrix\(([^\(]*)\)$/);
                if (matrixMatch) {
                  const matrix = matrixMatch[1].split(',').map(Number);
                  if (matrix.length === 6) {
                    mapContext.setTransform(...matrix);
                  }
                }
              }

              mapContext.drawImage(canvas, 0, 0);
            }
          });

          // 生成文件名（包含时间戳）
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
          const fileName = `地图导出_${timestamp}.png`;

          // 保存图片
          mapCanvas.toBlob((blob) => {
            if (blob) {
              // 使用现代浏览器的下载方式
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = fileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              console.log('地图图片导出成功:', fileName);
              this.$message.success('地图图片导出成功');
            } else {
              console.error('无法生成图片文件');
              this.$message.error('导出失败，无法生成图片文件');
            }
          }, 'image/png', 0.95);
        });

        // 触发地图重新渲染
        this.map.renderSync();

      } catch (error) {
        console.error('导出地图图片失败:', error);
        this.$message.error('导出失败，请重试');
      }
    },

    // 初始化地图
    initMap () {
      try {
        console.log('开始初始化地图...');
        // 检查DOM元素是否存在
        const targetElement = document.getElementById(this.mapId);
        if (!targetElement) {
          console.error('地图容器元素不存在，ID:', this.mapId);
          return;
        }
        console.log('地图容器元素找到:', targetElement);

        const center = this.getInitialCenter();
        console.log('地图中心点:', center);

        this.map = new Map({
          controls: [],
          interactions: defaultInteractions({
            doubleClickZoom: false // 禁用双击放大
          }),
          view: new View({
            center: center,
            projection: 'EPSG:4326',
            zoom: 14,
            maxZoom: 28,
            minZoom: 1,
            constrainResolution: true, // 限制分辨率为整数缩放级别，提升性能
            smoothResolutionConstraint: false // 禁用平滑分辨率约束，加快缩放
          }),
          target: this.mapId,
          layers: [],
          loadTilesWhileAnimating: true, // 动画时继续加载瓦片
          loadTilesWhileInteracting: true // 交互时继续加载瓦片
        });

        console.log('地图实例创建成功:', this.map);
        this.createBaseLayer();
        // 初始化比例尺
        this.initScaleLine();
        // 创建点击标记矢量图层
        this.createVectorLayer();
        this.initMeasureDistanceLayer();
        this.initMeasureSurfaceLayer();
        // 添加地图点击事件监听器
        this.mapClickListener = this.map.on('click', this.clickMap);
        console.log('地图初始化完成');
      } catch (error) {
        console.error('地图初始化失败:', error);
      }
    },

    // 初始化比例尺控件
    initScaleLine () {
      if (!this.map) {
        console.warn('GisMap: 地图实例为空，无法创建比例尺控件');
        return;
      }

      // 移除旧的比例尺控件
      if (this.scaleLineControl) {
        this.map.removeControl(this.scaleLineControl);
        this.scaleLineControl = null;
      }

      try {
        // 创建新的比例尺控件
        this.scaleLineControl = new ScaleLine({
          // 设置度量单位为米
          units: 'metric',
          target: 'scalebar',
          className: 'ol-scale-line'
        });

        // 添加到地图
        this.map.addControl(this.scaleLineControl);
        console.log('GisMap: 比例尺控件创建成功');
      } catch (error) {
        console.error('GisMap: 创建比例尺控件失败', error);
      }
    },

    // 中心定位
    getInitialCenter () {
      if (this.geo) {
        try {
          return JSON.parse(this.geo).coordinates;
        } catch (e) {
          console.error('解析geo属性失败:', e);
        }
      }
      return storage.get(LonLat);
    },

    // 加载底图
    createBaseLayer (config = {}) {
      // 支持旧的调用方式：createBaseLayer(url, projection, maxZoom)
      let url, projection, maxZoom, tileUrlFunction;

      if (typeof config === 'string') {
        // 旧的调用方式：第一个参数是URL字符串
        url = config;
        projection = arguments[1] || 'GCJ-02';
        maxZoom = arguments[2] || 18;
      } else {
        // 新的调用方式：传入配置对象
        url = config.mapUrl;
        projection = config.projection || 'GCJ-02';
        maxZoom = config.maxZoom || 18;
        tileUrlFunction = config.tileUrlFunction;
      }

      if (!url && !tileUrlFunction) {
        //默认必应地图
        projection = 'GCJ-02';
        maxZoom = 18;
        tileUrlFunction = (coord) => {
          return this.getVETileUrl(coord[0], coord[1], coord[2]);
        };
      }

      console.log('创建底图，使用投影:', projection, 'maxZoom:', maxZoom, 'URL:', url, 'tileUrlFunction:', !!tileUrlFunction);

      // 根据坐标系类型选择投影配置
      let projectionConfig = {};

      if (projection === 'GCJ-02') {
        // 使用火星坐标系（GCJ-02）
        // 检查是否已经注册过GCJ-02投影
        let gcjMecator = olProj.get('GCJ-02');

        if (!gcjMecator) {
          // 如果没有注册过，则创建并注册
          const gcj02Extent = [-20037508.342789244, -20037508.342789244, 20037508.342789244, 20037508.342789244];
          gcjMecator = new olProj.Projection({ code: 'GCJ-02', extent: gcj02Extent, units: 'm' });
          olProj.addProjection(gcjMecator);
          olProj.addCoordinateTransforms('EPSG:4326', gcjMecator, window.projzh.ll2gmerc, window.projzh.gmerc2ll);
          olProj.addCoordinateTransforms('EPSG:3857', gcjMecator, window.projzh.smerc2gmerc, window.projzh.gmerc2smerc);
          console.log('GCJ-02投影已注册');
        } else {
          console.log('GCJ-02投影已存在，直接使用');
        }

        projectionConfig = {
          projection: gcjMecator
        };
      } else {
        // 使用标准Web墨卡托投影（EPSG:3857）
        console.log('使用标准EPSG:3857投影');
        projectionConfig = {
          projection: 'EPSG:3857'
        };
      }

      // 构建 XYZ 数据源配置
      const xyzConfig = {
        ...projectionConfig,
        crossOrigin: 'anonymous',
        cacheSize: 2048, // 大幅增加缓存大小，从256提升到2048
        maxZoom: maxZoom,
        wrapX: true, // 启用水平环绕
        transition: 0, // 禁用瓦片加载过渡动画，加快渲染
        interpolate: false // 禁用图像平滑处理，加快渲染
      };

      // 根据是否有 tileUrlFunction 来决定使用哪种方式
      if (tileUrlFunction) {
        // 使用自定义瓦片URL函数（必应地图）
        xyzConfig.tileUrlFunction = tileUrlFunction;
        console.log('使用自定义 tileUrlFunction 创建底图');
      } else {
        // 使用URL模板（其他地图）
        xyzConfig.url = url;
      }

      this.baseLayer = new TileLayer({
        properties: {
          name: '底图',
          title: '地图图层',
          isBaseLayer: true
        },
        preload: 1, // 预加载1级瓦片
        source: new XYZ({
          ...xyzConfig,
          tileSize: 256,
          tilePixelRatio: 1, // 使用标准像素比，不使用高清瓦片
          reprojectionErrorThreshold: 0.5 // 重投影误差阈值，降低精度要求以提升速度
        }),
        useInterimTilesOnError: false, // 瓦片加载错误时不使用临时瓦片
        renderMode: 'image' // 使用image模式渲染，比vector快
      });

      this.map.getLayers().insertAt(0, this.baseLayer);
      console.log('底图创建完成，最大缩放级别:', maxZoom);
    },

    // 清除底图图层
    clearBaseLayer () {
      if (this.map && this.baseLayer) {
        // 清理底图数据源
        const source = this.baseLayer.getSource();
        if (source) {
          if (source.clear && typeof source.clear === 'function') {
            source.clear();
          }
          if (source.dispose && typeof source.dispose === 'function') {
            source.dispose();
          }
        }

        // 从地图中移除底图图层
        this.map.removeLayer(this.baseLayer);

        // 清理图层
        if (this.baseLayer.dispose && typeof this.baseLayer.dispose === 'function') {
          this.baseLayer.dispose();
        }

        this.baseLayer = null;
        console.log('底图图层已清除');
      }
    },

    // 必应地图瓦片URL生成器
    getVETileUrl (z, x, y) {
      let quadKey = '';
      let tileX = x;
      let tileY = y;

      for (let i = 0; i < z; i++) {
        quadKey = ((tileX & 1) + 2 * (tileY & 1)).toString() + quadKey;
        tileX >>= 1;
        tileY >>= 1;
      }

      return 'http://dynamic.t0.tiles.ditu.live.com/comp/ch/' + quadKey + '?it=G,VE,BX,L,LA&mkt=zh-cn,syr&n=z&og=111&ur=CN';
    },

    // 添加WMS图层
    getWmsLayers (item) {
      // 获取请求参数
      const params = {
        LAYERS: item.dataSource,
        FORMAT: 'image/png8',
        CQL_FILTER: item.showFilterCondition || ''
      };
      // 创建WMS源
      const wmsSource = new ImageWMS({
        crossOrigin: 'anonymous',
        params,
        serverType: 'geoserver',
        url: `/api/geoserver/${item.workspace}/wms`,
        hidpi: false
      });
      // 创建WMS图层
      const wmsLayer = new ImageLayer({
        id: item.id,
        name: item.name,
        crossOrigin: 'anonymous',
        projection: 'EPSG:4490',
        source: wmsSource,
        zIndex: item.layerShowSort || 99
      });
      // 添加WMS图层
      this.map.addLayer(wmsLayer);
      // 添加WMS点击事件
      this.clickWMS(wmsSource, item, wmsLayer);
    },

    // 移除WMS图层
    removeWmsLayers (item) {
      const layer = this.map.getLayers().getArray().find(layer => layer.values_.id === item.id);
      if (layer) {
        // 清理图层数据源
        const source = layer.getSource();
        if (source) {
          if (source.clear && typeof source.clear === 'function') {
            source.clear();
          }
          if (source.dispose && typeof source.dispose === 'function') {
            source.dispose();
          }
        }

        // 移除图层
        this.map.removeLayer(layer);

        // 清理图层
        if (layer.dispose && typeof layer.dispose === 'function') {
          layer.dispose();
        }
      }

      // 移除点击事件监听器
      if (this.clickTypeList[item.id]) {
        unByKey(this.clickTypeList[item.id]);
        this.clickTypeList[item.id] = null;
      }
    },

    // WMS点击事件
    clickWMS (wmsSource, item, wmsLayer) {
      const that = this;
      // 移除WMS点击事件
      unByKey(this.clickTypeList[item.id]);
      // 添加WMS点击事件
      this.clickTypeList[item.id] = this.map.on(
        'click',
        function (e) {
          // 如果正在进行测距或测面，不处理WMS点击事件（避免触发图例点击）
          if (that.activeToolType === 'measure-distance' || that.activeToolType === 'measure-surface') {
            return;
          }

          // 判断是否选中
          // 检查地图上是否有查询图层，如果有则只有查询图层可点击
          const hasQueryLayer = that.map.getLayers().getArray().some(layer =>
            layer.get('id') === 'query_in_query_drawer'
          );

          // 如果有查询图层，只有查询图层可点击；否则所有可见的 WMS 图层都可点击
          // 这样设备图层就不会阻挡 WMS 图层的点击了
          if (item.id === 'query_in_query_drawer' || (!hasQueryLayer && that.selectedItemId === item.id)) {
            console.log('WMS 图层点击触发:', item.name, 'ID:', item.id);
            const url = wmsSource.getFeatureInfoUrl(e.coordinate, that.map.getView().getResolution(), 'EPSG:4326', { INFO_FORMAT: 'application/json' });
            // 判断是否有要素（排除设备图层、测量图层、点击标记图层等，只检查与 WMS 相关的矢量要素）
            const hasWmsFeature = that.map.hasFeatureAtPixel(e.coordinate, {
              layerFilter: (layer) => {
                // 排除以下图层：设备图层、点击标记图层、测量图层
                return layer !== that.zoneDevicesLayer &&
                  layer !== that.vectorLayer &&
                  layer !== that.measureDistanceLayer &&
                  layer !== that.measureSurfaceLayer &&
                  layer !== that.zoneMeteringVectorLayer;
              }
            });
            if (!hasWmsFeature) {
              // 请求要素
              fetch(url).then((response) => { return response.json(); })
                .then((data) => {
                  console.log('WMS点击响应，图层:', item.name, '数据:', data.features[0]);
                  if (data.features[0]) {
                    const feature = data.features[0];

                    // 判断是否是片区范围示意图
                    const isZoneMeteringLayer =
                      item.name === '片区范围示意图' ||
                      item.dataSource === 'smart_water:zone_metering' ||
                      (item.dataSource && item.dataSource.includes('zone_metering'));

                    // 如果是片区范围示意图且有几何坐标，创建面图层
                    if (isZoneMeteringLayer && feature.geometry && feature.geometry.coordinates) {
                      that.showZoneMeteringPolygon(feature);
                    }

                    // 显示红色点击标记
                    that.clickData(feature);

                    // 传递要素选中事件
                    that.$emit('feature-selected', feature);
                  }
                });
            }
          }
        }
      );
    },

    // 点击要素，显示红色标记
    clickData (item) {
      console.log(item);

      this.clearVectorLayer();
      if (!item || !item.geometry) {
        return;
      }
      switch (item.geometry.type) {
        case 'LineString':
          this.clickLine(item);
          break;
        case 'Point':
          this.clickPoint(item);
          break;
      }
    },

    // 点击线要素
    clickLine (item) {
      if (!this.vectorSource || !item.geometry || !item.geometry.coordinates) {
        return;
      }
      const coordinates = item.geometry.coordinates;
      // 创建线要素
      const lineString = new LineString(coordinates);
      const lineFeature = new Feature({
        geometry: lineString,
        type: 'LineString'
      });
      // 添加到矢量图层
      this.vectorSource.addFeature(lineFeature);
    },

    // 点击点要素
    clickPoint (item) {
      if (!this.vectorSource || !item.geometry || !item.geometry.coordinates) {
        return;
      }

      // 处理坐标格式，确保是 [lng, lat] 格式
      let coordinates = item.geometry.coordinates;
      if (Array.isArray(coordinates) && coordinates.length >= 2) {
        const coord0 = coordinates[0];
        const coord1 = coordinates[1];

        // 判断坐标格式：如果第一个值在纬度范围内（18-54），第二个值在经度范围内（73-135），且第一个值 < 第二个值
        // 说明是 [lat, lng] 格式，需要转换为 [lng, lat]
        if (coord0 >= 18 && coord0 <= 54 && coord1 >= 73 && coord1 <= 135 && coord0 < coord1) {
          coordinates = [coord1, coord0]; // 交换坐标顺序
          console.log('点要素坐标已从 [lat, lng] 转换为 [lng, lat]');
        }
      }

      // 创建点要素
      const point = new Point(coordinates);
      const pointFeature = new Feature({
        geometry: point,
        type: 'Point'
      });
      // 添加到矢量图层
      this.vectorSource.addFeature(pointFeature);
      console.log('点要素已添加到地图，坐标:', coordinates);
    },

    // 创建点击标记矢量图层
    createVectorLayer () {
      this.vectorSource = new VectorSource();
      this.vectorLayer = new VectorLayer({
        source: this.vectorSource,
        zIndex: 1000, // 设置高层级
        style: new Style({
          fill: new Fill({
            color: 'rgba(255, 0, 0, 0.3)' // 半透明红色填充
          }),
          stroke: new Stroke({
            color: '#ff0000', // 红色边框
            width: 3
          }),
          image: new Circle({
            radius: 8,
            fill: new Fill({
              color: '#ff0000' // 红色填充
            }),
            stroke: new Stroke({
              color: '#ffffff', // 白色边框
              width: 2
            })
          })
        })
      });
      if (this.map) {
        this.map.addLayer(this.vectorLayer);
      }
    },

    // 清除点击标记矢量图层
    clearVectorLayer () {
      if (this.vectorSource) {
        this.vectorSource.clear();
      }
    },

    // 显示片区范围面图层
    showZoneMeteringPolygon (feature) {
      if (!this.map || !feature || !feature.geometry || !feature.geometry.coordinates) {
        console.warn('无法显示片区范围面图层: 缺少必要的数据');
        return;
      }

      try {
        // 如果已存在图层，先清除
        this.clearZoneMeteringPolygon();

        // 处理 GeoJSON 坐标格式
        // GeoJSON Polygon 的 coordinates 是三维数组: [[[lng, lat], ...]]
        // 或者可能是 [lat, lng] 顺序: [[[lat, lng], ...]]
        let coordinates = feature.geometry.coordinates;

        // 提取最外层的坐标数组（Polygon 的第一个环）
        if (Array.isArray(coordinates) && coordinates.length > 0) {
          coordinates = coordinates[0];
        }

        // 检查坐标格式，判断是否需要交换顺序
        // 如果第一个坐标的第一个值（纬度）小于第二个值（经度），且第一个值在合理的纬度范围内（18-54），第二个值在合理的经度范围内（73-135）
        // 说明是 [lat, lng] 格式，需要转换为 [lng, lat]
        if (coordinates.length > 0 && Array.isArray(coordinates[0]) && coordinates[0].length >= 2) {
          const firstCoord = coordinates[0];
          const lat = firstCoord[0];
          const lng = firstCoord[1];

          // 判断：如果第一个值在纬度范围内（18-54），第二个值在经度范围内（73-135），且第一个值 < 第二个值
          // 说明是 [lat, lng] 格式，需要交换为 [lng, lat]
          if (lat >= 18 && lat <= 54 && lng >= 73 && lng <= 135 && lat < lng) {
            // 交换坐标顺序：从 [lat, lng] 转为 [lng, lat]
            coordinates = coordinates.map(coord => [coord[1], coord[0]]);
            console.log('坐标格式已从 [lat, lng] 转换为 [lng, lat]');
          }
        }

        console.log('处理后的坐标:', coordinates);

        // 创建数据源
        this.zoneMeteringVectorSource = new VectorSource();

        // 创建多边形要素
        // Polygon 构造函数：coordinates 是数组的数组，每个内部数组是一个环
        // coordinates 已经是二维数组 [[lng, lat], [lng, lat], ...]，需要再包裹一层成为环
        const polygon = new Polygon([coordinates]);
        const polygonFeature = new Feature(polygon);

        // 设置要素属性（可选）
        if (feature.properties) {
          polygonFeature.setProperties(feature.properties);
        }

        // 添加到数据源
        this.zoneMeteringVectorSource.addFeature(polygonFeature);

        // 创建矢量图层，使用 EPSG:4326 投影
        this.zoneMeteringVectorLayer = new VectorLayer({
          source: this.zoneMeteringVectorSource,
          zIndex: 1000, // 设置高层级，确保显示在WMS图层上方
          style: new Style({
            stroke: new Stroke({
              color: '#000000', // 黑色边框
              width: 2
            })
            // 不设置 fill，只显示边框
          })
        });

        // 添加到地图
        this.map.addLayer(this.zoneMeteringVectorLayer);

        // 缩放到面图层范围
        const extent = this.zoneMeteringVectorSource.getExtent();
        if (extent && extent[0] !== Infinity) {
          this.map.getView().fit(extent, {
            padding: [50, 50, 50, 50],
            duration: 500
          });
        }

        console.log('片区范围面图层已创建并添加到地图，坐标数量:', coordinates.length);

        // 将坐标转换为 WKT 格式的 POLYGON，用于 WMS 查询
        // coordinates 已经是 [lng, lat] 格式的二维数组
        const coordinateString = coordinates.map(coord => `${coord[0]} ${coord[1]}`).join(',');
        const wktPolygon = `POLYGON((${coordinateString}))`;

        // 获取租户ID
        const tenantId = this.$store?.state?.user?.info?.tenantId || '';

        // 构建 CQL_FILTER
        const cqlFilter = `is_deleted = false and tenant_id = '${tenantId}' and WITHIN(geom, ${wktPolygon})`;

        console.log('WKT Polygon:', wktPolygon);
        console.log('CQL Filter:', cqlFilter);

        // 请求 gis_zone_line 图层
        const zoneLineLayer = {
          id: 'zone_metering_zone_line',
          name: '片区范围DN75管线',
          workspace: 'smart_water',
          dataSource: 'smart_water:gis_zone_line',
          showFilterCondition: cqlFilter,
          layerShowSort: 100
        };
        this.getWmsLayers(zoneLineLayer);

        // 请求 gis_pipe_network_zone 图层
        const pipeNetworkLayer = {
          id: 'zone_metering_pipe_network',
          name: '片区范围水表',
          workspace: 'smart_water',
          dataSource: 'smart_water:gis_pipe_network_zone',
          showFilterCondition: cqlFilter,
          layerShowSort: 101
        };
        this.getWmsLayers(pipeNetworkLayer);

      } catch (error) {
        console.error('创建片区范围面图层失败:', error);
        console.error('要素数据:', feature);
      }
    },

    // 清除片区范围面图层
    clearZoneMeteringPolygon () {
      // 清除关联的 WMS 图层
      this.removeWmsLayers({ id: 'zone_metering_zone_line' });
      this.removeWmsLayers({ id: 'zone_metering_pipe_network' });

      if (this.zoneMeteringVectorLayer && this.map) {
        // 从地图中移除图层
        this.map.removeLayer(this.zoneMeteringVectorLayer);

        // 清理数据源
        if (this.zoneMeteringVectorSource) {
          this.zoneMeteringVectorSource.clear();
          if (this.zoneMeteringVectorSource.dispose && typeof this.zoneMeteringVectorSource.dispose === 'function') {
            this.zoneMeteringVectorSource.dispose();
          }
          this.zoneMeteringVectorSource = null;
        }

        // 清理图层
        if (this.zoneMeteringVectorLayer.dispose && typeof this.zoneMeteringVectorLayer.dispose === 'function') {
          this.zoneMeteringVectorLayer.dispose();
        }
        this.zoneMeteringVectorLayer = null;

        console.log('片区范围面图层及相关WMS图层已清除');
      }
    },

    // 高亮显示片区设备
    highlightZoneDevices (devices) {
      console.log('开始高亮显示片区设备:', devices);

      // 先清除之前的设备图层
      this.clearZoneDevicesLayer();

      // 创建新的数据源和图层
      this.zoneDevicesSource = new VectorSource();

      // 添加分区计量仪表
      if (devices.remoteMeters && devices.remoteMeters.length > 0) {
        devices.remoteMeters.forEach(meter => {
          try {
            // 解析 GeoJSON 坐标
            const coordinates = this.parseGeoJsonCoordinates(meter.geoJson);
            if (coordinates && coordinates.length === 2) {
              const feature = new Feature({
                geometry: new Point([coordinates[0], coordinates[1]]),
                type: 'remoteMeter',
                data: meter
              });

              feature.setStyle(new Style({
                image: new Icon({
                  src: require('@/assets/myImg/1/流量计.png'),

                  scale: 0.4, // 使用缩放比例，与 WMS 保持一致
                  // anchor: [0.5, 1], // 锚点在底部中心
                  opacity: 1
                })
              }));

              this.zoneDevicesSource.addFeature(feature);
            }
          } catch (error) {
            console.error('解析分区计量仪表坐标失败:', meter, error);
          }
        });
      }

      // 添加分界阀门
      if (devices.boundaryValves && devices.boundaryValves.length > 0) {
        devices.boundaryValves.forEach(valve => {
          try {
            // 解析 GeoJSON 坐标
            const coordinates = this.parseGeoJsonCoordinates(valve.geoJson);
            if (coordinates && coordinates.length === 2) {
              const feature = new Feature({
                geometry: new Point([coordinates[0], coordinates[1]]),
                type: 'boundaryValve',
                data: valve
              });

              feature.setStyle(new Style({
                image: new Icon({
                  src: require('@/assets/myImg/1/icon-阀门深红@2x.png'),
                  scale: 0.4, // 使用缩放比例，与 WMS 保持一致
                  // anchor: [0.5, 1], // 锚点在底部中心
                  opacity: 1
                })
              }));

              this.zoneDevicesSource.addFeature(feature);
            }
          } catch (error) {
            console.error('解析分界阀门坐标失败:', valve, error);
          }
        });
      }

      // 创建矢量图层
      this.zoneDevicesLayer = new VectorLayer({
        source: this.zoneDevicesSource,
        zIndex: 1001, // 设置较高的层级，确保显示在上层
        visible: true
      });

      // 添加到地图
      if (this.map) {
        this.map.addLayer(this.zoneDevicesLayer);
        console.log(`已添加 ${devices.remoteMeters?.length || 0} 个分区计量仪表和 ${devices.boundaryValves?.length || 0} 个分界阀门到地图`);
      }
    },

    // 解析 GeoJSON 坐标
    parseGeoJsonCoordinates (geoJson) {
      if (!geoJson) {
        return null;
      }

      try {
        // 如果是字符串，先解析成对象
        const geoData = typeof geoJson === 'string' ? JSON.parse(geoJson) : geoJson;

        // 验证是否是有效的 Point 类型 GeoJSON
        if (geoData && geoData.type === 'Point' && Array.isArray(geoData.coordinates)) {
          return geoData.coordinates;
        }

        return null;
      } catch (error) {
        console.error('解析 GeoJSON 失败:', geoJson, error);
        return null;
      }
    },

    // 清除片区设备图层
    clearZoneDevicesLayer () {
      if (this.zoneDevicesLayer && this.map) {
        this.map.removeLayer(this.zoneDevicesLayer);

        if (this.zoneDevicesSource) {
          this.zoneDevicesSource.clear();
          if (this.zoneDevicesSource.dispose && typeof this.zoneDevicesSource.dispose === 'function') {
            this.zoneDevicesSource.dispose();
          }
          this.zoneDevicesSource = null;
        }

        if (this.zoneDevicesLayer.dispose && typeof this.zoneDevicesLayer.dispose === 'function') {
          this.zoneDevicesLayer.dispose();
        }
        this.zoneDevicesLayer = null;

        console.log('片区设备图层已清除');
      }
    },

    // 定位到单个设备
    locationToDevice (locationData) {
      console.log('定位到设备:', locationData);

      if (!locationData || !locationData.device) {
        console.warn('设备数据为空，无法定位');
        return;
      }

      const device = locationData.device;
      const icon = locationData.icon;

      // 解析设备坐标
      const coordinates = this.parseGeoJsonCoordinates(device.geoJson);

      if (!coordinates || coordinates.length !== 2) {
        console.warn('无法解析设备坐标:', device);
        return;
      }

      // 定位到设备位置
      this.setMapLocation(coordinates[0], coordinates[1], 18);

      // 在地图上添加高亮标记
      if (!this.zoneDevicesSource) {
        this.zoneDevicesSource = new VectorSource();
        this.zoneDevicesLayer = new VectorLayer({
          source: this.zoneDevicesSource,
          zIndex: 1001,
          visible: true
        });
        this.map.addLayer(this.zoneDevicesLayer);
      } else {
        // 清除之前的标记
        this.zoneDevicesSource.clear();
      }

      // 创建设备标记
      const feature = new Feature({
        geometry: new Point([coordinates[0], coordinates[1]]),
        type: locationData.type,
        data: device
      });

      feature.setStyle(new Style({
        image: new Icon({
          src: icon,
          scale: 0.5, // 稍微放大一点以突出显示
          opacity: 1
        })
      }));

      this.zoneDevicesSource.addFeature(feature);

      console.log('已定位到设备位置:', coordinates);
    },

    // 处理设备图标点击
    handleDeviceClick (feature) {
      const deviceData = feature.get('data');
      const deviceType = feature.get('type');

      if (!deviceData) {
        console.warn('设备数据为空');
        return;
      }

      // 将驼峰命名转换为下划线命名（与 featureConfig 保持一致）
      const convertedData = this.convertToSnakeCase(deviceData);

      // 构造符合 infoMap 组件要求的 feature 数据格式
      const featureData = {
        id: deviceType === 'remoteMeter' ? 'remote_water_meter' : 'zone_associated_point',
        properties: convertedData
      };

      console.log('触发设备详情显示:', featureData);

      // 触发 feature-selected 事件，通知父组件显示详情
      this.$emit('feature-selected', featureData);
    },

    // 将驼峰命名转换为下划线命名
    convertToSnakeCase (obj) {
      const result = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          // 将驼峰命名转换为下划线命名
          const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
          result[snakeKey] = obj[key];
        }
      }
      return result;
    }
  }
};
</script>

<style lang="less" scoped>
.map-container {
  width: 100%;
  height: 100%;
  position: relative;
  background: #fff;

  .map-content {
    width: 100%;
    height: 100%;
  }
}

// 确保地图图层不会产生闪烁效果
/deep/ .ol-viewport {
  .ol-layer {
    canvas {
      transition: none !important;
      filter: none !important;
    }
  }
}
</style>

<style lang="less">
// OpenLayers 比例尺全局样式（不 scoped，确保可以样式化动态创建的元素）
#scalebar .ol-scale-line {
  position: absolute !important;
  bottom: 55px !important;
  left: 0 !important;
  z-index: 1000 !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 4px !important;
  padding: 8px 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
}

#scalebar .ol-scale-line-inner {
  font-size: 12px !important;
  color: #333 !important;
  font-weight: 500 !important;
}
</style>
